<template>
  <el-dialog
    :title="surcharge.exists ? surcharge.name : 'New Surcharge'"
    visible
    width="80vw"
    top="5vh"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @close="$emit('update:isShow', false)"
  >
    <el-form
      ref="surchargeForm"
      v-loading="isSaving"
      :rules="validationRules"
      :model="surcharge"
      size="small"
    >
      <el-row :gutter="30">
        <el-col :md="12">
          <h1>
            General<br>
            <small>
              Description of general updates you're making
            </small>
          </h1>
          <el-row :gutter="15">
            <el-col>
              <el-form-item
                label="Name"
                prop="name"
              >
                <el-input
                  v-model="surcharge.name"
                  placeholder="Name"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="15">
            <el-col>
              <el-form-item
                label="External Name"
                prop="ext_name"
              >
                <el-input
                  v-model="surcharge.ext_name"
                  placeholder="External Name"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="15">
            <el-col>
              <el-form-item
                label="Short"
                prop="short"
              >
                <el-input
                  v-model="surcharge.short"
                  placeholder="Short"
                  clearable
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="Carrier"
                prop="carrier"
              >
                <el-select
                  v-model="surcharge.carrier"
                  filterable
                  remote
                  placeholder="Enter name of carrier"
                  class="w-100"
                  value-key="id"
                  :remote-method="remoteMethodCarrier"
                  :loading="isLoadingCarriers"
                  :disabled="surcharge.exists"
                >
                  <el-option
                    v-for="item in carriers"
                    :key="item.id"
                    :label="item.name"
                    :value="item"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="15">
            <el-col>
              <el-form-item
                label="Rule"
                prop="rule"
              >
                <el-select
                  v-model="surcharge.rule"
                  value-key="id"
                  filterable
                  placeholder="Select a rule"
                  :loading="isLoadingRules"
                  class="w-100"
                  clearable
                  @change="checkSurchargeRule"
                >
                  <el-option
                    v-for="rule in rules"
                    :key="rule.id"
                    :label="`${rule.desc} - ${rule.on}`"
                    :value="rule"
                  />
                  <el-option
                    label="Create New Rule"
                    value="new"
                    @click="isShowRuleForm = true"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <RuleForm
              v-if="isShowRuleForm"
              v-loading="isSavingRule"
              @close="isShowRuleForm = false"
              @save="createSurchargeRule"
            />
          </el-row>
          <el-row v-if="surcharge.rule">
            <el-card shadow="never">
              <div
                slot="header"
                class="clearfix"
              >
                <span>Expression</span>
              </div>
              <code>{{ surcharge.rule.expression || '-' }}</code>
            </el-card>
          </el-row>
          <el-row :gutter="15">
            <el-col :md="12">
              <el-form-item
                label="Buy Value"
                prop="buy_value"
              >
                <el-input-number
                  v-model="surcharge.buy_value"
                  :precision="4"
                  controls-position="right"
                  placeholder="Buy Value"
                  class="w-100"
                />
              </el-form-item>
            </el-col>
            <el-col :md="12">
              <el-form-item
                label="Buy Has Fuel"
                prop="buy_has_fuel"
              >
                <el-select
                  v-model="surcharge.buy_has_fuel"
                  placeholder="Buy Has Fuel"
                  class="w-100"
                >
                  <el-option
                    label="No"
                    :value="false"
                  />
                  <el-option
                    label="Yes"
                    :value="true"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <el-row :gutter="15">
            <el-col :md="12">
              <el-form-item
                label="Sell Value"
                prop="sell_value"
              >
                <el-input-number
                  v-model="surcharge.sell_value"
                  :precision="4"
                  controls-position="right"
                  placeholder="Sell Value"
                  class="w-100"
                />
              </el-form-item>
            </el-col>
            <el-col :md="12">
              <el-form-item
                label="Sell Has Fuel"
                prop="sell_has_fuel"
              >
                <el-select
                  v-model="surcharge.sell_has_fuel"
                  placeholder="Sell Has Fuel"
                  class="w-100"
                >
                  <el-option
                    label="No"
                    :value="false"
                  />
                  <el-option
                    label="Yes"
                    :value="true"
                  />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
        </el-col>
        <el-col :md="12">
          <h1>
            Dates<br>
            <small>
              Modify the dates and describe the implications
            </small>
          </h1>
          <el-row>
            <el-col>
              <el-form-item
                label="Start"
                prop="date_start"
              >
                <el-date-picker
                  v-model="surcharge.date_start"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a Start Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!surcharge.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-row>
            <el-col>
              <el-form-item
                label="End"
                prop="date_end"
              >
                <el-date-picker
                  v-model="surcharge.date_end"
                  type="date"
                  format="dd/MM/yyyy"
                  value-format="yyyy-MM-dd"
                  placeholder="Pick a End Date"
                  :picker-options="datePickerOption"
                  class="w-100"
                  :disabled="!surcharge.id"
                />
              </el-form-item>
            </el-col>
          </el-row>
          <el-divider />
          <el-row>
            <el-col>
              <h1>
                Active<br>
                <small>
                  This is a description of the implications of changing the active field + dates.<br>For example setting to active will automatically attempt to end the previous Surcharge (if only one is found).
                </small>
              </h1>
              <el-row>
                <el-col>
                  <el-form-item
                    label="Active"
                    prop="active"
                  >
                    <el-select
                      v-model="surcharge.active"
                      placeholder="Choose active or inactive"
                      class="w-100"
                      :disabled="!surcharge.id"
                    >
                      <el-option
                        label="Yes"
                        :value="true"
                      />
                      <el-option
                        label="No"
                        :value="false"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
              <el-divider />
              <el-row>
                <el-col>
                  <h1>
                    Audit<br>
                    <small :class="!surcharge.audit ? '' : 'text-red'">
                      When audit mode is enabled the surcharge will only apply to consignments created while impersonating the customer.
                    </small>
                  </h1>
                  <el-form-item
                    label="Audit Mode"
                    prop="audit"
                  >
                    <el-select
                      v-model="surcharge.audit"
                      placeholder="Choose enabled or disabled"
                      class="w-100"
                      :disabled="!surcharge.id"
                    >
                      <el-option
                        label="Enabled"
                        :value="true"
                      />
                      <el-option
                        label="Disabled"
                        :value="false"
                      />
                    </el-select>
                  </el-form-item>
                </el-col>
              </el-row>
            </el-col>
          </el-row>
        </el-col>
      </el-row>
    </el-form>
    <span
      slot="footer"
      class="dialog-footer"
    >
      <el-button @click.native="$emit('update:isShow', false)">
        Cancel
      </el-button>

      <el-button
        type="primary"
        :loading="isSaving"
        @click.native="surcharge.exists ? updateSurcharge() : createSurcharge()"
      >
        {{ surcharge.exists ? 'Save' : 'Create' }}
      </el-button>
    </span>
  </el-dialog>
</template>

<script>
import RuleForm from '@/components/Application/Manage/Surcharges/RuleForm.vue'

export default {
  components: { RuleForm },
  props: {
    isShow: {
      type: Boolean,
      required: true
    },
    surcharge: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      rules: [],
      isLoadingRules: false,
      isSaving: false,
      isFullscreen: false,
      isLoadingCarriers: false,
      isShowRuleForm: false,
      isSavingRule: false,
      carriers: [],
      datePickerOption: {
        shortcuts: [{
          text: 'Today',
          onClick (picker) {
            picker.$emit('pick', new Date())
          }
        }, {
          text: 'Yesterday',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24)
            picker.$emit('pick', date)
          }
        }, {
          text: 'A week ago',
          onClick (picker) {
            const date = new Date()
            date.setTime(date.getTime() - 3600 * 1000 * 24 * 7)
            picker.$emit('pick', date)
          }
        }]
      },
      validationRules: {
        name: [
          { required: true, message: 'Name of the rate surcharge is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        ext_name: [
          { required: true, message: 'External Name of the rate surcharge is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        short: [
          { required: true, message: 'Short of the rate surcharge is required', trigger: ['blur', 'change'] },
          { min: 5, max: 45, message: 'Length should be 5 to 45', trigger: ['blur', 'change'] }
        ],
        carrier: [
          { required: true, message: 'Carrier of the surcharge is required', trigger: 'change' }
        ]
      }
    }
  },
  created () {
    this.loadLists()
    if (this.surcharge.carrier) {
      this.remoteMethodCarrier(this.surcharge.carrier.name)
    }
  },
  methods: {
    createSurchargeRule (form) {
      this.isSavingRule = true
      this.$sdk.ratesurcharge_rule.create(form, builder => {
      })
        .then((res) => {
          this.$message({ message: 'Successfully saved', type: 'success' })
          this.loadLists()
          this.surcharge.rule = res
        })
        .finally(() => {
          this.isShowRuleForm = false
          this.isSavingRule = false
        })
      this.isSavingRule = true
    },
    checkSurchargeRule (entry) {
      if (entry === 'new') {
        this.isShowRuleForm = true
        this.surcharge.rule = null
      } else {
        this.isShowRuleForm = false
      }
    },
    loadLists () {
      this.isLoadingRules = true
      this
        .$sdk
        .ratesurcharge_rule
        .list(builder => {
          builder.limit = 500
          builder.order = { desc: 'asc' }
        })
        .then(res => { this.rules = res })
        .finally(() => { this.isLoadingRules = false })
    },
    createSurcharge () {
      this.$refs.surchargeForm.validate((valid) => {
        if (valid) {
          this.isSaving = true
          this.surcharge.group = new this.$sdk.ratesurcharge_group()
          this.surcharge.create(builder => {
            builder.include = ['carrier']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    updateSurcharge () {
      this.$refs.surchargeForm.validate((valid) => {
        if (valid) {
          this.isSaving = true

          if (this.surcharge.date_start) {
            this.surcharge.date_start = this.$moment(this.surcharge.date_start).set({ hour: 0, minute: 0, second: 0 }).toDate()
          }

          if (this.surcharge.date_end) {
            this.surcharge.date_end = this.$moment(this.surcharge.date_end).set({ hour: 23, minute: 59, second: 59 }).toDate()
          }

          this.surcharge.buy_value = this.surcharge.buy_value ? this.surcharge.buy_value : 0
          this.surcharge.sell_value = this.surcharge.sell_value ? this.surcharge.sell_value : 0

          this.surcharge.update(builder => {
            builder.include = ['carrier']
          })
            .then(() => this.$message({ message: 'Successfully saved', type: 'success' }))
            .finally(() => {
              this.isSaving = false
              this.$emit('saved')
              this.$emit('update:isShow', false)
            })
        }
      })
    },
    remoteMethodCarrier (query) {
      this.isLoadingCarriers = true
      this.$sdk.carrier.list(builder => {
        builder.filters = { 'carrier_name': query }
      }).then(res => {
        this.carriers = res
      }).finally(() => { this.isLoadingCarriers = false })
    }
  }
}
</script>

<style scoped>
.text-red {
  color: #f56c6c;
}
</style>
